/**
 * Personal card links arrive as `/box/:key/card/:cardKey?token=…`. The token is
 * a credential, so it is taken out of the address bar immediately and kept in
 * sessionStorage for the one claim that follows — otherwise it would survive in
 * history, in a copied URL, and in any screenshot of the page.
 *
 * The storage keys are santa-frontend's, so a visitor who starts in one version
 * of the app and reloads into the other keeps the token they arrived with.
 */

const TOKEN_KEY_PREFIX = 'santa_card_access_token:';
const UNAVAILABLE_KEY_PREFIX = 'santa_card_access_unavailable:';

function tokenKey(pathname: string): string {
  return `${TOKEN_KEY_PREFIX}${pathname}`;
}

function unavailableKey(pathname: string): string {
  return `${UNAVAILABLE_KEY_PREFIX}${pathname}`;
}

/**
 * Moves `?token=` from the URL into sessionStorage, leaving any other query
 * parameter (`?join=`) in place. Returns the token for this path, whether it
 * came from the URL now or from an earlier visit.
 */
export function takeCardAccessToken(pathname: string = window.location.pathname): string {
  if (typeof window === 'undefined') {
    return '';
  }

  const url = new URL(window.location.href);
  const fromUrl = url.searchParams.get('token');
  if (fromUrl && url.pathname === pathname) {
    sessionStorage.setItem(tokenKey(pathname), fromUrl);
    // A freshly issued link deserves a fresh attempt even if an older token for
    // the same card was rejected.
    sessionStorage.removeItem(unavailableKey(pathname));
    url.searchParams.delete('token');
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`,
    );
    return fromUrl;
  }

  return sessionStorage.getItem(tokenKey(pathname)) ?? '';
}

/** Called once a claim has gone through: the token is spent. */
export function clearCardAccessToken(pathname: string = window.location.pathname): void {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.removeItem(tokenKey(pathname));
  sessionStorage.removeItem(unavailableKey(pathname));
}

/**
 * Remembers that this token did not work, so a reload shows the recovery
 * options instead of failing the same claim again.
 */
export function markCardAccessUnavailable(pathname: string = window.location.pathname): void {
  if (typeof window === 'undefined') {
    return;
  }
  sessionStorage.setItem(unavailableKey(pathname), '1');
}

export function isCardAccessUnavailable(pathname: string = window.location.pathname): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return sessionStorage.getItem(unavailableKey(pathname)) === '1';
}

/** The invite key a visitor arrives with on a shared link. */
export function joinKeyFromUrl(): string {
  if (typeof window === 'undefined') {
    return '';
  }
  return new URLSearchParams(window.location.search).get('join') ?? '';
}

/** Preserve a pending claim when a legacy alias path becomes a short path. */
export function migrateCardAccessPath(previous: string, next: string): void {
  if (previous === next) return;
  try {
    for (const prefix of [TOKEN_KEY_PREFIX, UNAVAILABLE_KEY_PREFIX]) {
      const value = sessionStorage.getItem(`${prefix}${previous}`);
      if (value !== null) {
        sessionStorage.setItem(`${prefix}${next}`, value);
        sessionStorage.removeItem(`${prefix}${previous}`);
      }
    }
  } catch { /* URL credentials still work when session storage is unavailable. */ }
}
